import { useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { Link, useNavigate } from "@tanstack/react-router";
import { INSURANCES } from "@/data/heallexa";
import { useInView } from "@/hooks/useInView";
import { InsuranceLogo } from "@/components/forms/InsuranceLogo";

export function InsuranceSearchSection() {
  const { ref, inView } = useInView<HTMLDivElement>();
  const trackRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return INSURANCES;
    return INSURANCES.filter((i) => i.name.toLowerCase().includes(q));
  }, [query]);

  const goToSearch = (insuranceId: string) => {
    navigate({ to: "/search", search: { insurance: insuranceId } as any });
  };

  const scrollByPage = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth, behavior: "smooth" });
  };

  return (
    <section className="relative py-16 sm:py-20 border-b border-border">
      <div
        ref={ref}
        style={{ transitionDelay: "40ms" }}
        className={`mx-auto max-w-[1200px] px-4 sm:px-6 transition-all duration-700 ease-out ${
          inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}
      >
        <div className="flex flex-wrap items-end justify-between gap-4 sm:gap-6 mb-8">
          <div>
            <p className="text-[12px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-3">
              Search by insurance
            </p>
            <h2 className="text-[clamp(1.8rem,3vw,2.5rem)] font-bold tracking-tight text-foreground leading-[1.15]">
              Find a provider by insurance
            </h2>
            <p className="mt-3 text-[14px] text-muted-foreground max-w-[520px]">
              We cover {INSURANCES.length}+ plans across the United States.
            </p>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <button
              type="button"
              onClick={() => scrollByPage(-1)}
              aria-label="Previous insurance plans"
              className="h-9 w-9 sm:h-10 sm:w-10 inline-flex items-center justify-center border border-border text-foreground hover:bg-muted transition-colors rounded-md"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => scrollByPage(1)}
              aria-label="Next insurance plans"
              className="h-9 w-9 sm:h-10 sm:w-10 inline-flex items-center justify-center border border-border text-foreground hover:bg-muted transition-colors rounded-md"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="mb-10 flex items-stretch gap-2 max-w-[480px]">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden />
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                if (trackRef.current) trackRef.current.scrollLeft = 0;
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && filtered.length > 0) {
                  e.preventDefault();
                  goToSearch(filtered[0].id);
                }
              }}
              placeholder="Search insurance plans"
              aria-label="Search insurance plans"
              className="w-full h-11 pl-10 pr-4 text-[14px] border border-border rounded-md outline-none focus:border-primary transition-colors bg-background text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              if (filtered.length > 0) goToSearch(filtered[0].id);
            }}
            className="h-11 px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-md text-[14px] transition-colors cursor-pointer"
          >
            Search
          </button>
        </div>

        {filtered.length === 0 ? (
          <div className="p-10 text-center text-[14px] text-muted-foreground rounded-md border border-border">
            No insurance plans match &ldquo;{query}&rdquo;.
          </div>
        ) : (
          <div
            ref={trackRef}
            className="flex gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-4 -mx-4 px-4 sm:mx-0 sm:px-0"
            style={{ scrollbarWidth: "none" }}
          >
            {filtered.map((ins) => (
              <article
                key={ins.id}
                onClick={() => goToSearch(ins.id)}
                role="link"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    goToSearch(ins.id);
                  }
                }}
                className="group cursor-pointer bg-card border border-border/80 rounded-xl p-5 sm:p-6 flex flex-col snap-center shrink-0 w-[85vw] max-w-[320px] md:w-[calc((100%-2.5rem)/2)] lg:w-[calc((100%-3.75rem)/4)] md:max-w-none h-[220px] hover:border-primary/40 hover:shadow-sm transition-all"
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="shrink-0 h-12 w-12 rounded-lg overflow-hidden flex items-center justify-center border border-border/50 bg-background group-hover:scale-[1.05] transition-transform">
                    <InsuranceLogo id={ins.id} color={ins.color} />
                  </div>
                  <h3 className="font-semibold text-foreground text-[16px] leading-tight pt-1 min-w-0 pr-2">{ins.short}</h3>
                </div>

                <div className="flex-1">
                  <p className="text-[13px] text-muted-foreground line-clamp-2">{ins.name}</p>
                </div>

                <div className="mt-auto pt-4 border-t border-border/40 flex justify-end">
                  <button
                    type="button"
                    className="text-primary text-[13px] font-medium flex items-center gap-1 group-hover:gap-1.5 transition-all"
                    tabIndex={-1}
                  >
                    Browse
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="mt-6 flex justify-center">
          <Link
            to="/insurance"
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2 text-sm font-medium text-foreground hover:border-[#004e8c] hover:text-[#004e8c] transition-colors"
          >
            See all insurance plans
            <span aria-hidden>→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}
